// The one transport, and the policy every request carries.
//
// The generated client under ./gen knows the routes and the shapes and nothing
// else. What it does not know is decided here, once: where the API is mounted,
// how the caller proves who they are, and what a 401 means.
//
// # Two kinds of caller, on one device
//
// This is the only console in the shop whose transport carries two different
// credentials, because the product has two kinds of person on the same tablet.
//
//   - A **parent** is an account with an address and a password, kept by the
//     server itself (`authit`, the default). The bearer is the access token
//     `POST /auth/login` returned. `zitadel` is the same thing against a hosted
//     provider, for a deployment that already runs one; under `header` the
//     server runs its header verifier and the caller names their subject in
//     `X-Test-Subject` instead. Which one is in force is a build-time setting
//     (`VITE_AUTH_MODE`), never a runtime one, so a production bundle cannot be
//     talked into the test seam.
//   - A **child** is not an account and never will be (decision 0001). They
//     hold an opaque token that the PIN screen minted, and it goes in the
//     Authorization header in *every* mode — a child has no subject to name.
//
// The session decides which of the two is current; this file only asks.

import { client } from './gen/client.gen';

/** Where the API is mounted. The dev server proxies this to the Go binary. */
export const BASE = '/api';

export type AuthMode = 'authit' | 'header' | 'zitadel';

/**
 * How this build authenticates a parent. `authit` — the app's own accounts —
 * is the default, so a build that says nothing gets the provider the product
 * ships with rather than one it has to be given. `header` is the test seam.
 */
export const AUTH_MODE: AuthMode =
  import.meta.env.VITE_AUTH_MODE === 'header'
    ? 'header'
    : import.meta.env.VITE_AUTH_MODE === 'zitadel'
      ? 'zitadel'
      : 'authit';

/** The credential currently in force, or null when nobody is signed in. */
export type Credential =
  | { kind: 'parent'; value: string }
  | { kind: 'child'; value: string };

export type CredentialSource = () => Credential | null;

/**
 * Thrown for any non-2xx response. Carries the status and the parsed body,
 * because this server's refusals are data: a locked PIN, a plan that has run
 * out and a missing worksheet are three different things a screen says
 * differently, and they are told apart by `code`.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: Problem,
  ) {
    super(problem.message || problem.detail || problem.title || `HTTP ${status}`);
    this.name = 'ApiError';
  }

  /** The machine code, for the screens that branch on one. */
  get code(): string {
    return this.problem.code ?? '';
  }
}

/** The error shape the server answers with: Huma's model, plus a code. */
export interface Problem {
  code?: string;
  message?: string;
  title?: string;
  status?: number;
  detail?: string;
  details?: { message: string; location?: string; value?: unknown }[];
  errors?: { message: string; location?: string; value?: unknown }[];
}

/**
 * Points the generated client at the API and gives it the current credential.
 * Call once, before anything uses the SDK; the source is a function because
 * the credential changes several times an afternoon — parent, then child, then
 * a different child — and a value captured here would be the one that was
 * current when the app started.
 *
 * `X-Org-ID` is deliberately NOT set here. The document says it is required,
 * the generated types say so too, and every call site passes it through
 * `useOrg`. An interceptor that filled it in silently would make the type a lie.
 */
export function configureClient(
  credential: CredentialSource,
  onUnauthenticated: () => void,
): void {
  client.setConfig({ baseUrl: BASE });
  client.interceptors.request.use((request) => {
    const cred = credential();
    if (!cred) return request;
    if (cred.kind === 'child' || AUTH_MODE !== 'header') {
      request.headers.set('Authorization', `Bearer ${cred.value}`);
    } else {
      request.headers.set('X-Test-Subject', cred.value);
    }
    return request;
  });
  client.interceptors.response.use(async (response) => {
    if (response.status === 401) onUnauthenticated();
    return response;
  });
}

/**
 * Unwraps a generated call into its value, or throws {@link ApiError}.
 *
 * The SDK answers `{ data, error }` rather than throwing, which is the wrong
 * default for TanStack Query: a query that resolves with an error object is a
 * query that reports success. This is the adapter, in one place.
 */
export async function unwrap<T>(
  call: Promise<{ data?: T; error?: unknown; response?: Response }>,
): Promise<T> {
  const { data, error, response } = await call;
  if (!response) {
    throw new ApiError(0, { title: 'Netzwerkfehler', detail: String(error ?? 'request failed') });
  }
  if (!response.ok) {
    throw new ApiError(response.status, (error as Problem) ?? { status: response.status });
  }
  return data as T;
}

/**
 * A raw request for the three routes that carry bytes rather than a body an
 * OpenAPI operation can describe: the worksheet photograph, a spoken message,
 * and the multipart session start. It carries the same credential policy as
 * the generated client, because there is only one policy.
 */
export async function raw(
  path: string,
  init: RequestInit & { orgId?: string; credential?: Credential | null } = {},
): Promise<Response> {
  const headers = new Headers(init.headers);
  const cred = init.credential;
  if (cred) {
    if (cred.kind === 'child' || AUTH_MODE !== 'header') {
      headers.set('Authorization', `Bearer ${cred.value}`);
    } else {
      headers.set('X-Test-Subject', cred.value);
    }
  }
  if (init.orgId) headers.set('X-Org-ID', init.orgId);
  const response = await fetch(BASE + path, { ...init, headers });
  if (response.status === 401) throw new ApiError(401, { code: 'UNAUTHORIZED' });
  if (!response.ok) {
    let problem: Problem = { status: response.status };
    try {
      problem = (await response.clone().json()) as Problem;
    } catch {
      // not JSON; the status is all there is
    }
    throw new ApiError(response.status, problem);
  }
  return response;
}
